import { supabase } from './supabase'

/** Only the last N turns are sent, to keep token usage predictable. */
export const HISTORY_LIMIT = 10

/**
 * Ask Ginie. Returns the structured envelope from the edge function:
 * { intent, response, data, confidence, confirmation_required }
 */
export async function callGinie(message, careRecipientId, conversationHistory = []) {
  const { data, error } = await supabase.functions.invoke('ginie-chat', {
    body: {
      message,
      care_recipient_id: careRecipientId,
      conversation_history: conversationHistory.slice(-HISTORY_LIMIT),
    },
  })
  if (error) throw error
  // The function returns 4xx/5xx bodies with an `error` key rather than throwing.
  if (data?.error) throw new Error(data.error)
  return data
}

/** Human label for each intent, used on the confirmation card header. */
export const INTENT_LABEL = {
  log_care_note: { icon: '📝', label: 'Log care note' },
  log_vitals: { icon: '🩺', label: 'Log vitals' },
  log_incident: { icon: '⚠️', label: 'Log incident' },
  create_task: { icon: '✅', label: 'Create task' },
  create_appointment: { icon: '📅', label: 'Add appointment' },
  log_medication: { icon: '💊', label: 'Log medication' },
}

const SHIFT_LABEL = {
  morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening',
  night: 'Night', full_day: 'Full day',
}

const fmtDate = (iso) => {
  if (!iso) return null
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  const today = new Date().toLocaleDateString('en-CA')
  const label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return iso === today ? `Today, ${label}` : label
}

/**
 * Flatten an intent payload into label/value rows for the confirmation card,
 * so the family sees exactly what is about to be written.
 */
export function confirmationRows(intent, data = {}) {
  const rows = []
  const push = (label, value) => {
    if (value !== null && value !== undefined && value !== '' && value !== false) {
      rows.push({ label, value: String(value) })
    }
  }

  if (intent === 'log_care_note') {
    push('Date', fmtDate(data.log_date))
    push('Shift', SHIFT_LABEL[data.shift] || data.shift)
    const moods = [
      data.mood_morning && `AM ${data.mood_morning}/5`,
      data.mood_afternoon && `PM ${data.mood_afternoon}/5`,
      data.mood_evening && `Eve ${data.mood_evening}/5`,
    ].filter(Boolean)
    if (moods.length) push('Mood', moods.join(' · '))
    const meals = data.meals_json && typeof data.meals_json === 'object'
      ? Object.entries(data.meals_json)
          .filter(([, v]) => v?.status)
          .map(([k, v]) => `${k} ${String(v.status).replace('_', ' ')}`)
      : []
    if (meals.length) push('Meals', meals.join(' · '))
    if (data.fluids_ml) push('Fluids', `${Math.round(data.fluids_ml / 250)} glasses · ${data.fluids_ml}ml`)
    const m = data.movement_json
    if (m && typeof m === 'object') {
      push('Movement', [m.activity_type, m.duration_min && `${m.duration_min} min`].filter(Boolean).join(' · '))
    }
    const s = data.sleep_json
    if (s && typeof s === 'object') {
      push('Sleep', [s.hours && `${s.hours}h`, s.quality].filter(Boolean).join(' · '))
    }
    for (const [key, label] of [
      ['behaviour_fall', 'Fall'], ['behaviour_agitation', 'Agitation'],
      ['behaviour_confusion', 'Confusion'], ['behaviour_wandering', 'Wandering'],
    ]) {
      if (data[key]) push('Flagged', label)
    }
    push('Notes', data.notes)
  }

  if (intent === 'log_vitals') {
    if (data.bp_systolic && data.bp_diastolic) push('Blood pressure', `${data.bp_systolic}/${data.bp_diastolic}`)
    push('Blood glucose', data.blood_glucose && `${data.blood_glucose} mmol/L`)
    push('Fluids', data.fluids_ml && `${data.fluids_ml} ml`)
  }

  if (intent === 'log_incident') {
    push('Type', data.incident_type && String(data.incident_type).replace('_', ' '))
    push('Severity', data.severity)
    push('Injury', data.injury ? 'Yes' : null)
    push('What happened', data.description)
  }

  if (intent === 'create_task') {
    push('Task', data.title)
    push('Due', fmtDate(data.due_date))
    push('Details', data.description)
  }

  if (intent === 'create_appointment') {
    push('With', data.doctor_name)
    push('Specialty', data.specialty)
    push('Date', fmtDate(data.appointment_date))
    push('Time', data.appointment_time)
    push('Purpose', data.purpose)
  }

  if (intent === 'log_medication') {
    push('Medication', data.medication_name)
    push('Status', data.status)
  }

  return rows
}
